import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { useBase } from "@/contexts/BaseContext";

/**
 * Persistent local date + time chip, pinned top-LEFT on desktop and
 * mirrored into the mobile header. The clock now reflects the ACTIVE
 * BASE's country timezone (South Africa → SAST / Ghana → GMT) rather
 * than a hard-coded SAST, so the time always matches the base whose
 * data you're looking at.
 *
 * Timezone comes from `activeBaseMeta.timezone` (an IANA zone supplied
 * by the backend). We format via Intl.DateTimeFormat so DST and
 * arbitrary zones are always correct, refreshing once per minute.
 */
const DEFAULT_TZ = "Africa/Johannesburg";

export function DateTimeChip() {
  const { activeBaseMeta } = useBase();
  const tz = activeBaseMeta?.timezone || DEFAULT_TZ;
  const { time, dateShort, full, abbr } = useLiveZoneMinute(tz);
  return (
    <div
      className="fixed top-3 left-[264px] z-30 hidden lg:flex items-center gap-1.5 rounded-full bg-[color:var(--surface-glass-strong)] backdrop-blur-md border border-[color:var(--border-hairline)] px-2.5 py-1 text-[11px] shadow-sm"
      title={`Current base time — ${full} (${tz})`}
      data-testid="datetime-chip"
      aria-label={`Current base time. ${full}`}
    >
      <Clock className="h-3.5 w-3.5 shrink-0 text-[color:var(--accent-brass)]" strokeWidth={1.5} />
      <span className="font-medium tabular-nums" data-testid="datetime-chip-time">{time}</span>
      {abbr && <span className="text-[10px] text-muted-foreground">{abbr}</span>}
      <span className="text-muted-foreground border-l border-border/60 pl-1.5 leading-none">{dateShort}</span>
    </div>
  );
}

/**
 * Ultra-compact date/time pill for the mobile header.
 */
export function DateTimeChipMobile() {
  const { activeBaseMeta } = useBase();
  const tz = activeBaseMeta?.timezone || DEFAULT_TZ;
  const { time, full } = useLiveZoneMinute(tz);
  return (
    <div
      className="inline-flex items-center gap-1 rounded-full bg-[color:var(--surface-glass)] px-2 py-0.5 text-[11px]"
      title={`Current base time — ${full} (${tz})`}
      data-testid="datetime-chip-mobile"
    >
      <Clock className="h-3 w-3 text-[color:var(--accent-brass)]" strokeWidth={1.5} />
      <span className="tabular-nums font-medium">{time}</span>
    </div>
  );
}

/**
 * Returns formatted time / date strings for the given IANA timezone,
 * refreshed at the top of every clock-minute for a clean flip.
 */
function useLiveZoneMinute(tz) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const msUntilNextMinute = 60_000 - (Date.now() % 60_000);
    let interval;
    const kickOff = setTimeout(() => {
      setTick((t) => t + 1);
      interval = setInterval(() => setTick((t) => t + 1), 60_000);
    }, msUntilNextMinute);
    return () => { clearTimeout(kickOff); if (interval) clearInterval(interval); };
  }, [tz]);

  const now = new Date();
  const safeTz = (() => {
    try { new Intl.DateTimeFormat("en-GB", { timeZone: tz }); return tz; }
    catch (_e) { return DEFAULT_TZ; }
  })();

  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone: safeTz, hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(now);
  const dateShort = new Intl.DateTimeFormat("en-GB", {
    timeZone: safeTz, weekday: "short", day: "numeric", month: "short",
  }).format(now);
  const full = new Intl.DateTimeFormat("en-GB", {
    timeZone: safeTz, weekday: "long", day: "numeric", month: "long",
    year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(now);

  // Timezone abbreviation (e.g. SAST / GMT) when the platform provides it.
  let abbr = "";
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: safeTz, hour: "2-digit", timeZoneName: "short",
    }).formatToParts(now);
    abbr = parts.find((p) => p.type === "timeZoneName")?.value || "";
  } catch (_e) { /* noop */ }

  return { time, dateShort, full, abbr };
}
